import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import LinearProgress from '@mui/material/LinearProgress';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MovieIcon from '@mui/icons-material/Movie';
import DownloadIcon from '@mui/icons-material/Download';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import AuthContext from '../authAndConnections/auth';
import AxiosGlobal from '../authAndConnections/axiosGlobalUrl';
import { usePermissions } from '../../contextApi/PermissionContext';
import { fetchReadyToUpload, updateReadyToUpload, deleteReadyToUpload, actions } from '../../store/store';
import { copyText } from '../../tools/clipboard';
import { withExtension } from '../../tools/downloadName';
import ConfirmDialog from '../../tools/modal/confirmDialog';
import MediaViewer, { resolveEntryKind, downloadFile } from './mediaViewer';
import { MediaGrid, MediaGalleryViewer, toMediaItems } from './mediaGallery';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import DmFileEditDialog from './dmFileEditDialog';
import DmActivityLog from './dmActivityLog';
import RawContentChat from './rawContentChat';
import ProductVarietyPicker from './productVarietyPicker';
import CopyLinkButton from '../main/copyLinkButton';
import RestrictedAccessScreen from '../main/restrictedAccessScreen';
import UserAvatar from '../main/userAvatar';

// A stored file `name` may have had its extension stripped, so fall back to
// the disk filename's extension for the download (same helper as raw content).
const downloadFileName = (f) => withExtension(f.name || f.diskName, f.diskName);

export default function ReadyToUploadDetail({ id, onClose, onDeleted }) {
  const { t }  = useTranslation();
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const dispatch    = useDispatch();
  const history     = useHistory();
  const authCtx     = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);
  const { can }     = usePermissions();

  const doc = useSelector(s => s.dmSelectedReadyToUpload);
  const errorStatus = useSelector(s => s.dmSelectedReadyToUploadErrorStatus);

  const T = {
    BD:       isDark ? 'rgba(255,255,255,0.07)' : theme.palette.divider,
    TEXT_PRI: isDark ? 'rgba(255,255,255,0.87)' : theme.palette.text.primary,
    TEXT_SEC: isDark ? 'rgba(255,255,255,0.45)' : theme.palette.text.secondary,
    TEXT_TER: isDark ? 'rgba(255,255,255,0.2)'  : 'rgba(0,0,0,0.3)',
    CTRL_BG:  isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
    INPUT_BG: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
  };

  const [loading, setLoading] = useState(true);
  const [caption, setCaption] = useState('');
  const [captionDirty, setCaptionDirty] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [fileView, setFileView] = useState('grid');       // 'grid' | 'list'
  const [galleryIndex, setGalleryIndex] = useState(-1);   // -1 = viewer closed
  const mediaItems = useMemo(
    () => toMediaItems(doc?.files, axiosGlobal.defaultTargetApi),
    [doc?.files, axiosGlobal.defaultTargetApi]);
  const [editFile, setEditFile] = useState(null);
  const [confirmRemoveFile, setConfirmRemoveFile] = useState(null);
  const [confirmDeleteRecord, setConfirmDeleteRecord] = useState(false);
  const [viewerMedia, setViewerMedia] = useState(null);
  const [addProgress, setAddProgress] = useState(null);
  const [editingProducts, setEditingProducts] = useState(false);
  const [productsDraft, setProductsDraft] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    await dispatch(fetchReadyToUpload({ authCtx, axiosGlobal, id }));
    setLoading(false);
  }, [id, authCtx, axiosGlobal, dispatch]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (doc && String(doc._id) === String(id)) { setCaption(doc.caption || ''); setCaptionDirty(false); } }, [doc, id]);

  const saveCaption = async () => {
    if (!captionDirty) return;
    const fd = new FormData();
    fd.append('caption', caption);
    await dispatch(updateReadyToUpload({ authCtx, axiosGlobal, id, formData: fd }));
    setCaptionDirty(false);
  };

  const copyCaption = async () => {
    if (!caption) return;
    const copied = await copyText(caption);
    dispatch(actions.setShowSnackBar({
      status: true,
      msg: copied ? t('files.copied') : caption,
      type: copied ? 'success' : 'info',
    }));
  };

  const saveTitle = async () => {
    const fd = new FormData();
    fd.append('title', titleDraft);
    await dispatch(updateReadyToUpload({ authCtx, axiosGlobal, id, formData: fd }));
    setEditingTitle(false);
  };

  const openEditProducts = () => { setProductsDraft(doc.products || []); setEditingProducts(true); };
  const saveProducts = async () => {
    const fd = new FormData();
    fd.append('products', JSON.stringify(productsDraft.map((p) => ({ productId: p.productId, variantId: p.variantId }))));
    await dispatch(updateReadyToUpload({ authCtx, axiosGlobal, id, formData: fd }));
    setEditingProducts(false);
  };

  const removeFile = async (fileId) => {
    const fd = new FormData();
    fd.append('removeFileIds', JSON.stringify([fileId]));
    await dispatch(updateReadyToUpload({ authCtx, axiosGlobal, id, formData: fd }));
  };

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    setAddProgress(0);
    await dispatch(updateReadyToUpload({
      authCtx, axiosGlobal, id, formData: fd,
      onProgress: (e) => setAddProgress(e.total ? Math.round((100 * e.loaded) / e.total) : null),
    }));
    setAddProgress(null);
  };

  const handleDelete = async () => {
    await dispatch(deleteReadyToUpload({ authCtx, axiosGlobal, id }));
    onDeleted && onDeleted();
  };

  // Native download (real browser progress) + a separate fire-and-forget
  // authenticated call to record who downloaded it — same split as raw content.
  const handleDownload = (f) => {
    downloadFile(`${axiosGlobal.defaultTargetApi}/uploads/${f.diskName}`, downloadFileName(f));
    authCtx.jwtInst({ method: 'post',
      url: `${axiosGlobal.defaultTargetApi}/digitalMarketing/ready-to-upload/${id}/files/${f.fileId}/log-download` })
      .catch(() => {});
  };

  // In-app viewer — never window.open.
  const viewFile = (diskName, name, kindHint, mimetype) => {
    if (!diskName) return;
    setViewerMedia({
      url: `${axiosGlobal.defaultTargetApi}/uploads/${diskName}`,
      name: name || diskName,
      kind: kindHint || resolveEntryKind({ mimetype, diskName, name }),
    });
  };

  if (!loading && errorStatus === 403) return <RestrictedAccessScreen />;

  if (loading || !doc || String(doc._id) !== String(id)) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="60%" height={28} />
        <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: '10px' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ px: 3, pt: 2, pb: 1.5, borderBottom: `1px solid ${T.BD}`, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {editingTitle ? (
            <TextField size="small" autoFocus fullWidth value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={(e) => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
              placeholder={t('dm.titleLabel')}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '1rem', fontWeight: 700 } }} />
          ) : (
            <Typography
              onClick={() => can('digitalMarketing:readyToUpload:edit') && (setEditingTitle(true), setTitleDraft(doc.title || ''))}
              sx={{ fontSize: '1rem', fontWeight: 700, color: doc.title ? T.TEXT_PRI : T.TEXT_TER,
                cursor: can('digitalMarketing:readyToUpload:edit') ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {doc.title?.trim() || t('dm.filesReady', { count: doc.files?.length || 0 })}
              {can('digitalMarketing:readyToUpload:edit') && <EditOutlinedIcon sx={{ fontSize: 13, color: T.TEXT_TER }} />}
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <CopyLinkButton module="digitalMarketing" entityType="readyToUpload" entityId={doc._id} />
          <IconButton size="small" onClick={onClose} sx={{ color: T.TEXT_TER, flexShrink: 0 }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.25, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.78rem', color: T.TEXT_SEC }}>
            {doc.title?.trim() ? `${t('dm.fileCount', { count: doc.files?.length || 0 })} · ` : ''}
            {doc.language || '—'} · {doc.platform || '—'}
          </Typography>
          {doc.createdByName && (
            <>
              <Typography sx={{ fontSize: '0.78rem', color: T.TEXT_SEC }}>·</Typography>
              <UserAvatar userId={doc.createdBy} size={14} fontSize="0.5rem" />
              <Typography sx={{ fontSize: '0.78rem', color: T.TEXT_SEC }}>
                {t('crm.byActor', { name: doc.createdByName })}
              </Typography>
            </>
          )}
        </Box>

        {doc.rawContent && (
          <Box sx={{ mt: 1, p: 1, borderRadius: '8px', bgcolor: T.CTRL_BG, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography sx={{ fontSize: '0.68rem', color: T.TEXT_TER, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('dm.fromRawContentBatch')}
              </Typography>
              <Typography sx={{ fontSize: '0.76rem', color: T.TEXT_SEC, mt: 0.25 }}>
                {doc.rawContent.language} · {doc.rawContent.useCase} · {doc.rawContent.platform}
              </Typography>
            </Box>
            <Button size="small" variant="text" startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
              onClick={() => history.push(`/digitalMarketing?dm=raw&open=${doc.rawContentId}`)}
              sx={{ fontSize: '0.68rem', textTransform: 'none', borderRadius: '8px', flexShrink: 0 }}>
              {t('dm.openRawContent')}
            </Button>
          </Box>
        )}

        {can('digitalMarketing:readyToUpload:delete') && (
          <Button size="small" startIcon={<DeleteOutlineIcon sx={{ fontSize: 13 }} />} onClick={() => setConfirmDeleteRecord(true)}
            sx={{ fontSize: '0.7rem', textTransform: 'none', color: '#EA005A', mt: 1, px: 0 }}>
            {t('dm.deleteRecord')}
          </Button>
        )}
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 3, py: 2 }}>
        {/* ── Tagged Inventory varieties ── */}
        <Box sx={{ mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: T.TEXT_TER, flexGrow: 1 }}>
              {t('dm.taggedVarietiesLabel')}
            </Typography>
            {can('digitalMarketing:readyToUpload:edit') && !editingProducts && (
              <IconButton size="small" onClick={openEditProducts} sx={{ color: T.TEXT_TER, width: 22, height: 22 }}>
                <EditOutlinedIcon sx={{ fontSize: 13 }} />
              </IconButton>
            )}
          </Box>
          {editingProducts ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <ProductVarietyPicker value={productsDraft} onChange={setProductsDraft} T={T} />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="contained" onClick={saveProducts}
                  sx={{ fontSize: '0.72rem', textTransform: 'none', borderRadius: '8px' }}>{t('common.save')}</Button>
                <Button size="small" onClick={() => setEditingProducts(false)}
                  sx={{ fontSize: '0.72rem', textTransform: 'none', color: T.TEXT_SEC }}>{t('common.cancel')}</Button>
              </Box>
            </Box>
          ) : (doc.products || []).length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {doc.products.map((p) => (
                <Tooltip key={String(p.variantId)} title={[p.productName, p.branchName].filter(Boolean).join(' · ')}>
                  <Chip size="small" label={p.code}
                    sx={{ height: 24, fontSize: '0.72rem', bgcolor: T.CTRL_BG, color: T.TEXT_PRI, fontFamily: 'monospace' }} />
                </Tooltip>
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontSize: '0.78rem', color: T.TEXT_TER, fontStyle: 'italic' }}>
              {t('dm.noneTaggedYet')}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1,
            textTransform: 'uppercase', color: T.TEXT_TER }}>
            {t('dm.filesLabel')}
          </Typography>
          {(doc.files || []).length > 0 && (
            <ToggleButtonGroup size="small" exclusive value={fileView}
              onChange={(_, v) => v && setFileView(v)} sx={{ ml: 'auto' }}>
              <ToggleButton value="grid" sx={{ px: 0.9, py: 0.15 }}>
                <Tooltip title={t('dm.mediaViewGrid')}><GridViewIcon sx={{ fontSize: 14 }} /></Tooltip>
              </ToggleButton>
              <ToggleButton value="list" sx={{ px: 0.9, py: 0.15 }}>
                <Tooltip title={t('dm.mediaViewList')}><ViewListIcon sx={{ fontSize: 14 }} /></Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        {fileView === 'grid' && mediaItems.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <MediaGrid items={mediaItems} T={T}
              onOpen={(it) => setGalleryIndex(mediaItems.findIndex((m) => m.id === it.id))} />
          </Box>
        )}

        <Box sx={{ display: fileView === 'list' ? 'flex' : 'none', flexDirection: 'column', gap: 1, mb: 1.5 }}>
          {(doc.files || []).map((f) => (
            <Box key={f.fileId} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.1,
              borderRadius: '10px', bgcolor: T.CTRL_BG, border: `1px solid ${T.BD}` }}>
              {(f.mimetype || '').startsWith('video/')
                ? <MovieIcon sx={{ fontSize: 15, color: T.TEXT_TER, flexShrink: 0 }} />
                : <InsertDriveFileIcon sx={{ fontSize: 15, color: T.TEXT_TER, flexShrink: 0 }} />}
              <Typography onClick={() => viewFile(f.diskName, f.name, undefined, f.mimetype)} noWrap
                sx={{ fontSize: '0.78rem', color: T.TEXT_PRI, flexGrow: 1, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { textDecoration: 'underline' } }}>
                <OpenInNewIcon sx={{ fontSize: 12, flexShrink: 0 }} /> {f.name || t('dm.previewFileFallback')}
              </Typography>
              {f.diskName && (
                <Tooltip title={t('common.download')}>
                  <IconButton size="small" onClick={() => handleDownload(f)} sx={{ color: T.TEXT_SEC, width: 24, height: 24 }}>
                    <DownloadIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
              {can('digitalMarketing:readyToUpload:edit') && (
                <Tooltip title={t('dm.editFileTip')}>
                  <IconButton size="small" onClick={() => setEditFile(f)} sx={{ color: T.TEXT_SEC, width: 24, height: 24 }}>
                    <EditOutlinedIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
              {can('digitalMarketing:readyToUpload:edit') && (
                <Tooltip title={t('dm.removeTip')}>
                  <IconButton size="small" onClick={() => setConfirmRemoveFile(f.fileId)} sx={{ color: '#EA005A', width: 24, height: 24 }}>
                    <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>

        {can('digitalMarketing:readyToUpload:edit') && (
          <Box sx={{ mb: 2 }}>
            <Button component="label" size="small" startIcon={<AddPhotoAlternateIcon sx={{ fontSize: 14 }} />}
              disabled={addProgress !== null}
              sx={{ fontSize: '0.72rem', textTransform: 'none', color: T.TEXT_SEC }}>
              {t('dm.addFiles')}
              <input type="file" hidden multiple onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
            </Button>
            {addProgress !== null && (
              <Box sx={{ mt: 0.75 }}>
                <LinearProgress variant="determinate" value={addProgress} sx={{ borderRadius: 2, height: 5 }} />
                <Typography sx={{ fontSize: '0.65rem', color: T.TEXT_TER, mt: 0.25 }}>
                  {t('dm.uploadingPercent', { percent: addProgress })}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
            color: T.TEXT_TER }}>
            {t('dm.captionSectionLabel')}
          </Typography>
          <Tooltip title={t('common.copyToClipboard')}>
            <span>
              <IconButton size="small" onClick={copyCaption} disabled={!caption}
                sx={{ color: T.TEXT_TER, width: 24, height: 24, '&:hover': { color: T.TEXT_PRI } }}>
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <TextField fullWidth multiline minRows={3} size="small" value={caption}
          disabled={!can('digitalMarketing:readyToUpload:edit')}
          onChange={(e) => { setCaption(e.target.value); setCaptionDirty(true); }}
          onBlur={saveCaption}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: T.INPUT_BG, borderRadius: '10px', fontSize: '0.8rem' } }} />

        {/* ── Activity (who viewed / downloaded) ── */}
        <Box sx={{ mt: 2.5, mb: 1 }}>
          <DmActivityLog endpointBase="ready-to-upload" id={doc._id} T={T} />
        </Box>
      </Box>

      {/* ── Chat — a graduated record reuses the SAME thread as its source raw
          content batch (one conversation about this content, not a forked
          parallel chat); a standalone record (no rawContentId) gets its own
          thread keyed by its own id instead. Rendered by RawContentChat
          itself, which carries its own px:3 padding + border-top, matching
          the raw-content detail exactly. ── */}
      {can('digitalMarketing:rawContent:chat') && (
        <Box sx={{ flexShrink: 0, overflowY: 'auto', maxHeight: '45%' }}>
          {doc.rawContentId
            ? <RawContentChat rawContentId={doc.rawContentId} T={T} isDark={isDark} />
            : <RawContentChat readyToUploadId={doc._id} T={T} isDark={isDark} />}
        </Box>
      )}

      <DmFileEditDialog open={Boolean(editFile)} onClose={() => setEditFile(null)}
        file={editFile} recordId={doc._id} kind="readyToUpload" />

      <MediaViewer open={Boolean(viewerMedia)} onClose={() => setViewerMedia(null)} media={viewerMedia} />

      <MediaGalleryViewer open={galleryIndex >= 0} items={mediaItems} index={galleryIndex}
        onIndexChange={setGalleryIndex} onClose={() => setGalleryIndex(-1)} />

      <ConfirmDialog
        open={Boolean(confirmRemoveFile)}
        onClose={() => setConfirmRemoveFile(null)}
        onConfirm={() => removeFile(confirmRemoveFile)}
        title={t('dm.removeFileTitle')}
        message={t('dm.removeFileMessage')}
        confirmLabel={t('common.remove')}
        destructive
      />
      <ConfirmDialog
        open={confirmDeleteRecord}
        onClose={() => setConfirmDeleteRecord(false)}
        onConfirm={handleDelete}
        title={t('dm.deleteRecordTitle')}
        message={t('dm.deleteRecordMessage')}
        confirmLabel={t('common.delete')}
        destructive
      />
    </Box>
  );
}
